import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Typography, Space, message } from 'antd';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Chip from '@mui/material/Chip';
import { AddRounded, CloseOutlined } from "@mui/icons-material";
import { X } from 'lucide-react';
import {
    removeExchangePortfolioId,
    clearExchangePortfolioId
} from '../../store/slices/exchange.slice';
import { addPortfoliosToCompany } from '../../api/companyApi';

const { Title, Text } = Typography;

const ExchangeCart = () => {
    const dispatch = useDispatch();
    const exchangeType = useSelector(state => state.exchange.exchangeType);
    const selectedUserPortfolioIds = useSelector(state => state.exchange.selectedUserPortfolioIds);
    const selectedCompanyPortfolioIds = useSelector(state => state.exchange.selectedCompanyPortfolioIds);
    const portfolios = useSelector(state => state.portfolios.items || []);
    const userId = useSelector(state => state.user.data?.id);
    const companyId = useSelector(state => state.user.data?.company_details?.company_id);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectedIds = exchangeType === 'user'
        ? selectedUserPortfolioIds
        : selectedCompanyPortfolioIds;

    const selectedPortfolios = portfolios.filter(portfolio =>
        selectedIds.includes(portfolio.id)
    );

    const handleRemovePortfolio = (portfolioId) => {
        dispatch(removeExchangePortfolioId(portfolioId));
    };

    const handleClearAll = () => {
        dispatch(clearExchangePortfolioId());
    };

    const handleSubmit = async () => {
        if (selectedIds.length === 0) return;

        setIsSubmitting(true);
        try {
            if (exchangeType === 'company') {
                dispatch(addPortfoliosToCompany({
                    userId,
                    companyId,
                    portfolioIds: selectedIds,
                })).then(() => {
                    message.success('Portfolios successfully attached to company');
                });
                dispatch(clearExchangePortfolioId());
            } else {
            }
        } catch (error) {
            message.error(error.message || 'Failed to attach portfolios');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (selectedPortfolios.length === 0) {
        return (
            <Card className="mt-4 bg-gray-100 rounded-xl" bordered={false}>
                <div className="text-center p-4 flex flex-col">
                    <div className={'text-lg font-semibold'}>
                        No portfolios selected for {exchangeType === 'user' ? 'user' : 'company'} exchange
                    </div>
                    <div className={'text-md text-gray-600'}>
                            Select Portfolios Below and Select the "Add" button to start using them.
                    </div>
                </div>
            </Card>
        );
    }
    let buttonText = "No Portfolios Selected";
    if (selectedPortfolios.length > 0) {
        const count = selectedPortfolios.length;
        buttonText = exchangeType === 'user' ? `Add (${count}) to My List` : `Add (${count}) to Company List`;
    }

    return (
        <Card className="mt-4 bg-gray-100 rounded-xl gapless px-4 py-3" bordered={false}>
            <Space direction="vertical" className="w-full">
                <div className="flex justify-start items-center mb-4">
                    <Title level={4} className="m-0">
                        Selected Portfolios ({selectedPortfolios.length})
                    </Title>
                    <ButtonGroup className={'mx-4'}>
                        <Button
                            variant={'text'}
                            startIcon={<AddRounded />}
                            loading={isSubmitting}
                            disabled={selectedPortfolios.length === 0 || isSubmitting}
                            color={selectedPortfolios.length === 0 ? 'primary' : 'info'}
                            onClick={handleSubmit}
                        >
                            <span className={'font-semibold'}>
                                {isSubmitting ? 'Processing...' : buttonText}
                            </span>
                        </Button>
                        <Button
                            startIcon={<CloseOutlined />}
                            color="error"
                            onClick={handleClearAll}
                            disabled={isSubmitting}
                            variant="text"
                        >
                            Clear ({selectedPortfolios.length})
                        </Button>
                    </ButtonGroup>
                </div>
                <div className="flex flex-wrap gap-2 py-3">
                    {selectedPortfolios.map(portfolio => (
                        <Chip
                            key={portfolio.id}
                            label={
                                <div className="flex items-center gap-2">
                                    <span>{portfolio.name}</span>
                                    <span className="text-xs text-gray-500">
                                        ({portfolio.targets?.equity}/{portfolio.targets?.fixed})
                                    </span>
                                </div>
                            }
                            onDelete={() => handleRemovePortfolio(portfolio.id)}
                            deleteIcon={<X size={16} />}
                            disabled={isSubmitting}
                            className="!bg-white !border !border-gray-800"
                            sx={{
                                backgroundColor: '#fff',
                                '& .MuiChip-label': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                },
                            }}
                        />
                    ))}
                </div>
            </Space>
        </Card>
    );
};

export default ExchangeCart;